import Vue from 'vue';
import { Module, Mutation, VuexModule, Action } from 'vuex-module-decorators';
import doctorStore from './doctor';
import Store from '@/store';
import { ExportLock } from '@/models/doctor';

export enum Status {
  Loading = 'loading',
  Authenticated = 'authenticated',
  Unauthenticated = 'unauthenticated'
}

export type LoginArguments = {
  email: string;
  password: string;
}

@Module({
  dynamic: true,
  store: Store,
  name: 'auth',
  namespaced: true,
})
export default class Auth extends VuexModule {
  status: Status = Status.Loading;
  token: string = '';
  centerIdOverride: string = '';
  exportLock: ExportLock = ExportLock.None;

  // In case of auth center id and selected center id differ cross center
  // resource request will fail. Temporary selected center ID override
  // can help bypass the issue. Race conditions might appear.
  // TODO: This will require a proper solution later on. I warned you.
  ignoreCenterIdOverride: boolean = false;

  cookieAlias = {
    token: 'hemo_api_token',
    centerId: 'hemo_api_selected_center_id'
  };

  // ---------------------------------------------------------------------------
  // MUTATIONS
  // ---------------------------------------------------------------------------

  @Mutation
  setLoading() {
    this.status = Status.Loading;
  }

  @Mutation
  setAuthenticated(token: string) {
    console.log(`[Auth] Added, updated`);
    this.token = token;
    this.status = Status.Authenticated;
    Vue.cookies.set(this.cookieAlias.token, token, {});
  }

  @Mutation
  setUnauthenticated() {
    console.log(`[Auth] Removed`);
    this.status = Status.Unauthenticated;
    this.token = '';
    Vue.cookies.remove(this.cookieAlias.token);
    this.centerIdOverride = '';
    Vue.cookies.remove(this.cookieAlias.centerId);
  }

  @Mutation
  setCenterId(id: string) {
    if (!id) {
      this.centerIdOverride = '';
      Vue.cookies.remove(this.cookieAlias.centerId);
      return;
    }

    this.centerIdOverride = id;
    Vue.cookies.set(this.cookieAlias.centerId, id);
  }

  @Mutation
  setIgnoreCenterIdOverride(value: boolean) {
    this.ignoreCenterIdOverride = value;
  }

  @Mutation
  setExportLock(to: ExportLock) {
    console.log(`[Auth] Export lock changed to ${to}`);
    this.exportLock = to;
  }

  // ---------------------------------------------------------------------------
  // GETTERS
  // ---------------------------------------------------------------------------

  public get isAuthenticated() {
    return this.status === Status.Authenticated;
  }

  public get currentCenterId() {
    if (this.centerIdOverride && !this.ignoreCenterIdOverride) {
      return this.centerIdOverride;
    }

    return this.authCenterId;
  }

  // Information about the authenticated center.
  public get authCenterId() {
    return this.payload.centerId || null;
  }

  public get hasPrimaryCenter() {
    return this.payload.isPrimaryCenter || false;
  }

  public get expiry() {
    return this.payload.exp ? parseInt(this.payload.exp as any, 10) : undefined;
  }

  public get isPrimaryCenter() {
    // Cross-center view cannot be primary center.
    if (this.currentCenterId !== this.authCenterId) return false;
    return this.payload.isPrimaryCenter || false;
  }

  public get isDeveloperCenter() {
    return this.payload.isDeveloperCenter || false;
  }

  // Information about the authenticated doctor.
  public get authDoctorId() {
    return this.payload.doctorId || null;
  }

  public get authPatientId() {
    return this.payload.patientId || null;
  }

  public get isManager() {
    return this.payload.isManager || false;
  }

  public get isDoctor() {
    return this.payload.type === 'doctors';
  }

  public get isPatient() {
    return this.payload.type === 'patients';
  }

  public get hasExportLock() {
    return !!this.exportLock && this.exportLock != ExportLock.None;
  }

  public get refreshToken() {
    return this.payload.refreshToken;
  }

  private get payload(): Partial<{
    centerId: string;
    isPrimaryCenter: boolean;
    isDeveloperCenter: boolean;
    doctorId?: string;
    patientId?: string;
    isManager: boolean;
    type: 'doctors' | 'patients';
    refreshToken: string;
    iat: number;
    exp: number;
  }> {
    if (!this.token) {
      return {};
    }
    return JSON.parse(
      decodeURIComponent(
        atob(
          this.token
            .split('.', 3)[1]
            .replace(/-/g, '+')
            .replace(/_/g, '/')
        )
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Check if token exists or not and then updates the state accordingly.
   */
  @Action
  resume() {
    const signToken =  new URL(location.href).searchParams.get('signToken');
    const token = signToken || Vue.cookies.get(this.cookieAlias.token);

    if (!token) {
      this.context.commit('setUnauthenticated');
      return;
    }

    this.context.commit('setAuthenticated', token);
    this.context.commit('setCenterId', Vue.cookies.get(this.cookieAlias.centerId));
  }

  /**
   * Sets the center to fire request as.
   */
  @Action
  changeCenter(centerId: string) {
    if (!this.hasPrimaryCenter) {
      throw new Error('only the primary center can act as another');
    }
    this.context.commit('setCenterId', centerId);
  }
}
